import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import PreviewIcon from '@mui/icons-material/Preview'
import PsychologyIcon from '@mui/icons-material/Psychology'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import {Grid, LinearProgress} from '@mui/material'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton, {IconButtonProps} from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {blueGrey, grey, red} from '@mui/material/colors'
import {styled} from '@mui/material/styles'
import {useAppNavigate} from 'app/hooks/navigation/useAppNavigate'
import {ISavedSearchConcept} from 'app/models/saved_search'
import * as React from 'react'
import {useSelector} from 'react-redux'
import {useLocation, useSearchParams} from 'react-router-dom'
import {selectSavedSearchSlice} from 'redux/selectors/savedSearchSelector'
import ShowMoreText from '../ShowMoreText'
import DraggableConceptsList from '../concepts/DraggableConceptsList'
import KeywordList from '../keywords/KeywordList'
import SaveSearchToProjectDialog from '../projects/SaveSearchToProjectDialog'
import ConceptsAutoComplete from './../concepts/ConceptsAutoComplete'
import IgnoredConceptsDialog from './IgnoredConceptsDialog'
import IncludedConceptsDialog from './IncludedConceptsDialog'
import KeywordsDialog from './KeywordsDialog'
import {useToolbarContext} from './ToolbarProvider'

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean
  setMarginLeft: boolean
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const {expand, setMarginLeft, ...other} = props
  return <IconButton {...other} />
})(({theme, expand, setMarginLeft}) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: setMarginLeft ? 'auto' : 'undefined',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

interface Props {
  conceptList?: ISavedSearchConcept[]
}

export default function Toolbar({conceptList}: Props) {
  const navigate = useAppNavigate()
  const location = useLocation()
  const saveSearchRedux = useSelector(selectSavedSearchSlice)
  const [showConceptsDialog, setShowConceptsDialog] = React.useState<boolean>(false)
  const [showIgnoredConcepts, setShowIgnoredConcepts] = React.useState<boolean>(false)
  const [showRevisions, setShowRevisions] = React.useState<boolean>(false)
  const [showKeywords, setShowKeywords] = React.useState<boolean>(false)
  const {isDirty, setReRun, reRun, expanded, setExpanded} = useToolbarContext()
  const [reanalyzing, setReanalyzing] = React.useState(false)
  const [showProjectsDialog, setShowProjectsDialog] = React.useState<boolean>(false)
  const [searchParams] = useSearchParams()

  //this handles chevron click backa nd forth
  const handleExpandClick = () => {
    setExpanded(!expanded)
  }
  //this handles anytime a click outside toolbar card within main page
  const handleClickAway = () => {
    setExpanded(false)
  }

  const viewResults = () => {
    if (location.pathname.includes(`/discovery/${saveSearchRedux?.savedSearch?.id}/results`)) {
      setReRun(true)
    } else {
      navigate(`/discovery/${saveSearchRedux?.savedSearch?.id}/results`)
    }
  }

  const conceptsPillList = React.useMemo(() => {
    return (
      <CardContent sx={{ml: 2, p: 0}}>
        {saveSearchRedux?.savedSearch?.keywords &&
          saveSearchRedux.savedSearch.keywords.length > 0 && (
            <Box display={'flex'} justifyContent={'space-between'} justifyItems={'center'}>
              <Typography variant={'button'}>
                Current Search Keywords:
                <br />
                <span style={{color: grey[500], fontSize: '1.5em'}}>
                  <KeywordList />
                </span>
              </Typography>
            </Box>
          )}
        <Divider sx={{m: 2}} />
        <Box display={'flex'} justifyContent={'space-between'} justifyItems={'center'}>
          <Typography variant={'button'} sx={{color: 'inherit'}}>
            Current Search Concepts:
          </Typography>
        </Box>
        <DraggableConceptsList />
        <Divider sx={{m: 2}} />
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box display='flex' justifyContent='flex-start'>
              <ConceptsAutoComplete parentOwned={false} placeholder={'Add A Concept or Keyword'} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    )
  }, [showProjectsDialog, saveSearchRedux.savedSearch, isDirty, setReRun, reRun])

  const ConceptsIgnoredIcon = () => {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <PsychologyIcon fontSize='large' sx={{color: blueGrey[300]}} />
        <NotInterestedIcon fontSize={'large'} sx={{position: 'absolute', color: red[900]}} />
      </Box>
    )
  }

  //started with a return from business and concepts, then added results, now adding from concept detail
  //so to start simplifying, just going to add a returl to send here and a less smart button to go back.
  //I'm leaving these 3 other cases as are and just providing an override with retval
  const navBack = searchParams.get('nav')
  //for some reason replacing the first + with & results in spaces? Must be something with the hook in how + is used in useLocation hook, idk, so replacing space afterwords
  const retval = location.search.includes('retval')
    ? searchParams.get('retval')?.replaceAll('+', '&').replaceAll(' ', '&')
    : undefined

  const onResults = location.pathname.includes('results')
  const onBusinessDirectory = location.pathname.includes('businesses/all')
  const onConceptDirectory = location.pathname.includes('concepts/all')
  const onPersonDirectory = location.pathname.includes('people/all')
  const onInnovationDirectory = location.pathname.includes('innovations/all')
  const onAwardDirectory = location.pathname.includes('awards/all')
  const onOpportunityDirectory = location.pathname.includes('opportunities/all')
  const onProjectsDirectory = location.pathname.includes('projects/list')
  const onRecentSearches = location.pathname.includes('discovery/list')
  const onHome = location.pathname.includes('dashboard')
  const onAcount = location.pathname.includes('account')
  const fromSearchDirectory = searchParams.get('fromSearchDirectory')

  const hasRetUrlResults = searchParams.get('fromDiscoveryRequestUniqueId')
  //when on Business detail, we have multiple tabs where entities can be drilled into, so a retval is set from those entities
  const hasRetUrlBusDir = !retval && searchParams.get('fromBusinessDirectory')
  const hasRetUrlConDir = searchParams.get('fromConceptDirectory')
  const hasRetUrlPersonDir = searchParams.get('fromPersonDirectory')
  const hasRetUrlInnovationDir = searchParams.get('fromInnovationDirectory')
  const hasRetUrlAwardDir = searchParams.get('fromAwardDirectory')
  const hasRetUrlOpportDir = searchParams.get('fromOpportunityDirectory')

  //define return path based on what params we got
  const discoveryRequestId = saveSearchRedux?.savedSearch?.id
    ? saveSearchRedux.savedSearch?.id
    : hasRetUrlResults
    ? hasRetUrlResults
    : undefined

  //get page
  const page = searchParams.get('page')
  const tab = searchParams.get('tab')

  const retPath =
    discoveryRequestId &&
    !hasRetUrlBusDir &&
    !hasRetUrlConDir &&
    !hasRetUrlPersonDir &&
    !hasRetUrlInnovationDir &&
    !hasRetUrlAwardDir &&
    !hasRetUrlOpportDir
      ? `/discovery/${discoveryRequestId}/results/${tab ? tab : ''}`
      : hasRetUrlBusDir
      ? `/businesses/all${hasRetUrlBusDir !== 'tru' ? '?search=' + hasRetUrlBusDir : ''}`
      : hasRetUrlConDir
      ? `/concepts/all${hasRetUrlConDir !== 'tru' ? '?search=' + hasRetUrlConDir : ''}`
      : hasRetUrlPersonDir
      ? `/people/all${hasRetUrlPersonDir !== 'tru' ? '?search=' + hasRetUrlPersonDir : ''}`
      : hasRetUrlInnovationDir
      ? `/innovations/all${
          hasRetUrlInnovationDir !== 'tru' ? '?search=' + hasRetUrlInnovationDir : ''
        }`
      : hasRetUrlAwardDir
      ? `/awards/all${hasRetUrlAwardDir !== 'tru' ? '?search=' + hasRetUrlAwardDir : ''}`
      : hasRetUrlOpportDir
      ? `/opportunities/all${hasRetUrlOpportDir !== 'tru' ? '?search=' + hasRetUrlOpportDir : ''}`
      : undefined
  //if it's back to results, pop off the last bit to get the tab or result type to go back to
  const arrPath = hasRetUrlResults
    ? location.pathname.split('/').filter((r) => r !== '')
    : undefined
  //to track active tab i added !tab to this in order to leave the result type...tho I don't think it's being used, but tab is from results to track active tab
  const resultType = arrPath && !tab ? arrPath.pop() : undefined

  //get scroll position (will get one from results)
  const scrollPosition = searchParams.get('scrollPosition')
  const retUrl =
    scrollPosition && resultType
      ? retPath +
        resultType +
        (retPath ? `${retPath.includes('?') ? '&' : '?'}scrollPosition=${scrollPosition}` : '') +
        (page ? `&page=${page}` : '')
      : retPath && resultType
      ? retPath + resultType + (page ? `${retPath.includes('?') ? '&' : '?'}page=${page}` : '')
      : retPath && scrollPosition
      ? retPath +
        `${retPath.includes('?') ? '&' : '?'}scrollPosition=${scrollPosition}` +
        (page ? `&page=${page}` : '')
      : retPath
      ? retPath + (page ? `${retPath.includes('?') ? '&' : '?'}page=${page}` : '')
      : scrollPosition && !retPath && discoveryRequestId
      ? `/discovery/${discoveryRequestId}/results/business?scrollPosition=${scrollPosition}` +
        (page ? `&page=${page}` : '')
      : undefined

  const showBackToResults =
    retUrl &&
    !retval &&
    hasRetUrlResults &&
    !onResults &&
    !onBusinessDirectory &&
    discoveryRequestId &&
    !hasRetUrlBusDir &&
    !hasRetUrlConDir &&
    !onConceptDirectory &&
    !onPersonDirectory &&
    !onInnovationDirectory &&
    !onAwardDirectory &&
    !onOpportunityDirectory

  const showBackToDirectory =
    retUrl &&
    !onResults &&
    !onBusinessDirectory &&
    !onConceptDirectory &&
    !onPersonDirectory &&
    !onInnovationDirectory &&
    !onAwardDirectory &&
    !onOpportunityDirectory &&
    (hasRetUrlBusDir ||
      hasRetUrlConDir ||
      hasRetUrlPersonDir ||
      hasRetUrlAwardDir ||
      hasRetUrlInnovationDir ||
      hasRetUrlOpportDir)

  //these for being able to show the back button whenever we are not on directory, or loading a search, or doing new search
  const onDirectory =
    onResults ||
    onBusinessDirectory ||
    onConceptDirectory ||
    onPersonDirectory ||
    onInnovationDirectory ||
    onAwardDirectory ||
    onOpportunityDirectory ||
    onProjectsDirectory ||
    onRecentSearches ||
    fromSearchDirectory === 'tru' ||
    onHome ||
    onAcount

  return saveSearchRedux?.status === 'loading' ? (
    <LinearProgress variant={'query'} />
  ) : (saveSearchRedux.savedSearch || showBackToResults || showBackToDirectory || retval) &&
    !onHome &&
    !onAcount ? (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Card sx={{bgcolor: grey[50], boxShadow: 'none', p: 0, m: 0}}>
        <CardActions sx={{diplay: 'flex', justifyContent: 'space-between'}} disableSpacing>
          <Box>
            {showBackToResults ? (
              <Button
                onClick={() => navigate(retUrl)}
                startIcon={<ArrowBackIcon />}
                variant={'outlined'}
              >
                Back to Results
              </Button>
            ) : showBackToDirectory ? (
              <Button
                onClick={() => (navBack ? navigate(-1) : navigate(retUrl))}
                startIcon={<ArrowBackIcon />}
                variant={'outlined'}
              >
                Back to Directory
              </Button>
            ) : retval ? (
              <Button
                onClick={() => navigate(retval)}
                startIcon={<ArrowBackIcon />}
                variant={'outlined'}
              >
                Go Back
              </Button>
            ) : !onDirectory ? (
              <Button
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIcon />}
                variant={'outlined'}
              >
                Back
              </Button>
            ) : (
              <></>
            )}
            {saveSearchRedux.savedSearch && (
              <>
                <LibraryAddIcon
                  onClick={() => setShowProjectsDialog(true)}
                  sx={{ml: 2, mr: 2, cursor: 'pointer'}}
                />

                <SaveSearchToProjectDialog
                  show={showProjectsDialog}
                  setShow={setShowProjectsDialog}
                  discoveryRequest={saveSearchRedux.savedSearch}
                />
              </>
            )}

            {saveSearchRedux.savedSearch?.concepts &&
            (saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock !== 'exclude')?.length ??
              0 > 0) ? (
              <Tooltip title='Number of Concepts'>
                <IconButton
                  aria-label='Concepts'
                  onClick={() => setShowConceptsDialog(true)}
                  disableRipple
                >
                  <Badge
                    badgeContent={
                      saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock !== 'exclude')
                        ?.length ?? '0'
                    }
                    color={
                      saveSearchRedux.savedSearch?.concepts &&
                      (saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock !== 'exclude')
                        ?.length ??
                        0 > 0)
                        ? 'primary'
                        : 'default'
                    }
                  >
                    <PsychologyIcon fontSize={'large'} />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
            {saveSearchRedux.savedSearch?.concepts &&
            (saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock === 'exclude')?.length ??
              0 > 0) ? (
              <Tooltip title='Ignored Concepts'>
                <IconButton
                  aria-label='Ignored Concepts'
                  onClick={() => setShowIgnoredConcepts(true)}
                  disableRipple
                >
                  <Badge
                    badgeContent={
                      saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock === 'exclude')
                        ?.length ?? '0'
                    }
                    color={
                      saveSearchRedux.savedSearch?.concepts &&
                      (saveSearchRedux.savedSearch?.concepts?.filter((i) => i.__lock === 'exclude')
                        ?.length ??
                        0 > 0)
                        ? 'primary'
                        : 'default'
                    }
                  >
                    <ConceptsIgnoredIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
            {saveSearchRedux.savedSearch &&
            (saveSearchRedux.savedSearch?.keywords?.length ?? 0 > 0) ? (
              <Tooltip title='Current Search Keywords'>
                <IconButton
                  aria-label='Keywords'
                  onClick={() => setShowKeywords(true)}
                  disableRipple
                >
                  <Badge
                    badgeContent={saveSearchRedux.savedSearch?.keywords?.length ?? '0'}
                    color={'primary'}
                  >
                    <FilterListIcon sx={{color: blueGrey[300]}} />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}

            {saveSearchRedux.savedSearch?.search_text && (
              <Tooltip title='Current Search input string text'>
                <IconButton aria-label='Concepts' disableRipple>
                  <Typography variant={'overline'} fontWeight={700} color={grey[900]} mr={1}>
                    Search Input:
                  </Typography>
                  <Typography variant={'overline'}>
                    {
                      <ShowMoreText
                        variant={'body1'}
                        fontSize={'1em'}
                        text={saveSearchRedux.savedSearch?.search_text}
                        maxLen={50}
                        dialog={true}
                        moreString='...Show More'
                        lessString='...Show Less'
                      />
                    }
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
            {saveSearchRedux.savedSearch?.name && (
              <Box component={'span'} ml={3}>
                <Typography variant={'overline'} fontWeight={700}>
                  Search Name:
                </Typography>
                <Typography variant={'overline'} color={grey[700]} ml={1}>
                  {saveSearchRedux.savedSearch?.name}
                </Typography>
              </Box>
            )}
          </Box>

          <Box>
            {saveSearchRedux.savedSearch?.id ? (
              // when not on search page
              !location.pathname.includes(
                `/discovery/${saveSearchRedux.savedSearch?.id}/results`
              ) ? (
                <Box>
                  <Tooltip title='View Search Results'>
                    <IconButton sx={{marginLeft: 'auto'}} onClick={viewResults}>
                      <Badge
                        badgeContent={<SyncProblemIcon fontSize='small' color={'error'} />}
                        invisible={!isDirty}
                      >
                        <PreviewIcon color={isDirty ? 'warning' : 'primary'} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <ExpandMore
                    expand={expanded}
                    onClick={handleExpandClick}
                    aria-expanded={expanded}
                    aria-label='show more'
                    setMarginLeft={false}
                  >
                    <ExpandMoreIcon fontSize='large' />
                  </ExpandMore>
                </Box>
              ) : (
                <Box>
                  <ExpandMore
                    expand={expanded}
                    onClick={handleExpandClick}
                    aria-expanded={expanded}
                    aria-label='show more'
                    setMarginLeft={false}
                  >
                    <ExpandMoreIcon fontSize='large' />
                  </ExpandMore>
                </Box>
              )
            ) : (
              <></>
            )}
          </Box>
        </CardActions>
        {saveSearchRedux.savedSearch?.concepts && (
          <>
            <Collapse in={expanded}>{conceptsPillList}</Collapse>
          </>
        )}
        <IgnoredConceptsDialog show={showIgnoredConcepts} setShow={setShowIgnoredConcepts} />
        <IncludedConceptsDialog show={showConceptsDialog} setShow={setShowConceptsDialog} />

        <KeywordsDialog show={showKeywords} setShow={setShowKeywords} />
      </Card>
    </ClickAwayListener>
  ) : null
}
